/*
  Month names are spelt here rather than asked of the locale. Chrome's ICU gives en-GB's September
  as "Sept", four letters against every other month's three, so a column of dates changed width
  every September and nothing matched the design (ALS-223). Three letters, always.
*/
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (n: number) => String(n).padStart(2, '0');

/** "Sep": a month in three letters, whatever the browser's locale data says. */
export function shortMonth(date: Date): string {
    return MONTHS[date.getMonth()];
}

/** A Date from what the API sends, or null when it is not one. */
function parse(value: string | Date): Date | null {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/** "03 Sep 2026, 10:00", the register's date-and-time style; the raw value when it is not a date. */
export function formatWhen(iso: string): string {
    const date = parse(iso);
    return date
        ? `${formatDayMonth(date)} ${date.getFullYear()}, ${pad2(date.getHours())}:${pad2(date.getMinutes())}`
        : iso;
}

/** "05 Sep": a day without its year, for a range that states the year once. */
export function formatDayMonth(date: Date): string {
    return `${pad2(date.getDate())} ${shortMonth(date)}`;
}

/**
 * "16-Jun-2026", the way the prototype's Orders screen and its date field write a day. Kept to
 * those two places rather than every date in the admin: the prototype's audit columns write
 * "23 Nov 2021", and so does `formatDate` (ALS-223).
 */
export function formatDashedDate(value: string | Date): string {
    const date = parse(value);
    return date
        ? `${pad2(date.getDate())}-${shortMonth(date)}-${date.getFullYear()}`
        : String(value);
}

/** "20-Jun", the Orders register's due note, which has no year because it is always near today. */
export function formatDashedDayMonth(date: Date): string {
    return `${pad2(date.getDate())}-${shortMonth(date)}`;
}

/**
 * "£1,690.00", "US$890.00": an amount in its currency, the way the design prints a price. An
 * unknown code falls back to the code itself before the figure rather than throwing, since the
 * legacy currency table is editable.
 */
export function formatMoney(amount: number, currency = 'GBP', fractionDigits = 2): string {
    try {
        return amount.toLocaleString('en-GB', {
            style: 'currency',
            currency,
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        });
    } catch {
        return `${currency} ${amount.toLocaleString('en-GB', {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        })}`;
    }
}

/**
 * "1.17", "0.791234", "1": an exchange rate with no trailing zeros, to the six places the order
 * stores, as the invoice and the credit note print it ("0.######").
 */
export function formatRate(rate: number): string {
    return rate.toFixed(6).replace(/\.?0+$/, '');
}

/**
 * "742 B", "28 KB", "1.4 MB": a file's weight in the shortest unit that is still honest, which is
 * one decimal at most. It steps down through the units rather than printing megabytes always,
 * because a 28 KB upload rendered as "0.0 MB" tells the reader nothing about the file they just
 * chose.
 */
export function formatBytes(bytes: number | null | undefined): string {
    if (bytes === null || bytes === undefined || bytes < 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
}

/** "23 Nov 2021", the design's audit-column date; the raw value when it is not a date. */
export function formatDate(iso: string): string {
    const date = parse(iso);
    return date ? `${formatDayMonth(date)} ${date.getFullYear()}` : iso;
}

/**
 * Today's ISO day in London, which is the day the server judges dates against: the database's
 * clock runs on GMT Standard Time. A browser elsewhere would otherwise call London's today the
 * future, or yesterday's today.
 */
export function isoToday(now: Date = new Date()): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
